import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import api from '../utils/restaurantApi';
import { 
  PlusIcon, 
  TrashIcon, 
  MagnifyingGlassIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  MapPinIcon,
  CurrencyDollarIcon,
  PencilIcon
} from '@heroicons/react/24/outline';
import { 
  searchLandmarks,
  setDeliveryFees,
  formatCurrency 
} from '../utils/restaurantApi';
import { jwtDecode } from 'jwt-decode';
import { showSuccessNotification, showErrorNotification, showInfoNotification, showWarningNotification } from './NotificationManager';
const DeliveryFeeManager = ({ onClose }) => {
  const [landmarks, setLandmarks] = useState([]);
  const [searchResults, setSearchResults] = useState([]);
  const [currentFees, setCurrentFees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchLoading, setSearchLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [newFees, setNewFees] = useState([]);
  const [defaultFee, setDefaultFee] = useState('');
  
  const restaurantCredentials = React.useMemo(() => {
    const token = localStorage.getItem("token");
    if (!token) return null;
    
    try {
      const decodedToken = jwtDecode(token);
      return {
        token,
        customId: decodedToken.restaurant?.customId || decodedToken.customId
      };
    } catch (error) {
      console.error("Error decoding token:", error);
      return null;
    }
  }, []);

  // Load current fees and landmarks
  useEffect(() => {
    const loadData = async () => {
      if (!restaurantCredentials?.customId) {
        showErrorNotification("Authentication required");
        return;
      }
      setLoading(true);
      try {
        // Get delivery fees (API returns { success, deliveryFees: [...] })
        const feesResponse = await api.get(`/delivery-fees/restaurant/${restaurantCredentials.customId}`);
        const feesData = Array.isArray(feesResponse.data?.deliveryFees) ? feesResponse.data.deliveryFees : [];
        setCurrentFees(feesData);

        // Get universities (API returns { status, data: [...] })
        const universitiesResponse = await api.get('/universities');
        const universities = Array.isArray(universitiesResponse.data?.data) ? universitiesResponse.data.data : [];

        // Flatten all landmarks with university info
        const allLandmarks = universities.flatMap(uni =>
          (uni.nearestLandmarks || []).map(landmark => ({
            name: landmark,
            university: uni.name,
            id: `${uni._id}-${landmark}`,
            universityId: uni._id
          }))
        );
        setLandmarks(allLandmarks);

        // Set default fee if exists
        const defaultFeeItem = feesData.find(fee => fee?.isDefault);
        if (defaultFeeItem) {
          setDefaultFee(defaultFeeItem.fee.toString());
        } else {
          setDefaultFee('');
        }
      } catch (error) {
        console.error('Error loading data:', error);
        showErrorNotification('Failed to load delivery fee data');
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [restaurantCredentials]);

  // Search landmarks
  const handleSearch = useCallback(async (query) => {
    if (!query.trim()) {
      setSearchResults([]);
      return;
    }

    setSearchLoading(true);
    try {
      // Use the updated searchLandmarks function that searches across all universities
      const results = await searchLandmarks(query);
      setSearchResults(results.filter(result => 
        // Filter out landmarks that already have fees set
        !currentFees.some(fee => fee.landmark === result.name) &&
        !newFees.some(fee => fee.landmark === result.name)
      ));
    } catch (error) {
      console.error('Search error:', error);
      showErrorNotification('Failed to search landmarks');
    } finally {
      setSearchLoading(false);
    }
  }, [currentFees, newFees]);

  // Add new fee
  const addFee = (landmark) => {
    if (newFees.find(fee => fee.landmark === landmark.name) || 
        currentFees.find(fee => fee.landmark === landmark.name)) {
      showWarningNotification('Fee for this landmark already exists');
      return;
    }

    setNewFees(prev => [...prev, {
      landmark: landmark.name,
      fee: '',
      isDefault: false,
      university: landmark.university
    }]);
    setSearchQuery('');
    setSearchResults([]);
  };

  // Remove new fee
  const removeFee = (index) => {
    setNewFees(prev => prev.filter((_, i) => i !== index));
  };

  // Update fee amount
  const updateFeeAmount = (index, amount) => {
    setNewFees(prev => prev.map((fee, i) => 
      i === index ? { ...fee, fee: amount } : fee
    ));
  };

  // Save fees
  const saveFees = async () => {
    if (!restaurantCredentials) {
      showErrorNotification("Authentication required");
      return;
    }

    // Validate fees
    const validFees = newFees.filter(fee => fee.fee && parseFloat(fee.fee) >= 0);
    if (validFees.length === 0 && !defaultFee) {
      showWarningNotification('Please add at least one delivery fee');
      return;
    }

    setSaving(true);
    try {

      // Save landmark specific fees
      const savePromises = validFees.map(fee =>
        setDeliveryFees(
          restaurantCredentials.customId,
          fee.landmark,
          parseFloat(fee.fee)
        )
      );

      // Add default fee if set
      if (defaultFee && parseFloat(defaultFee) >= 0) {
        savePromises.push(
          setDeliveryFees(
            restaurantCredentials.customId,
            "DEFAULT",
            parseFloat(defaultFee)
          )
        );
      }

      await Promise.all(savePromises);
      showSuccessNotification('Delivery fees updated successfully!');

      // Refresh current fees using api directly
      const updatedFeesResponse = await api.get(`/delivery-fees/restaurant/${restaurantCredentials.customId}`);
      const updatedFees = updatedFeesResponse.data?.deliveryFees || [];
      setCurrentFees(updatedFees);
      setNewFees([]);

      // Close modal after successful save
      setTimeout(() => {
        onClose?.();
      }, 1500);

    } catch (error) {
      console.error('Error saving fees:', error);
      showErrorNotification('Failed to save delivery fees');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white p-8 rounded-xl max-w-md w-full mx-4">
          <div className="flex items-center justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-cheese"></div>
            <span className="ml-3 text-gray-600">Loading delivery fee settings...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        className="bg-white rounded-xl max-w-4xl w-full max-h-[90vh] overflow-hidden"
      >
        {/* Header */}
        <div className="bg-gradient-to-r from-cheese to-yellow-400 text-crust p-6">
          <div className="flex justify-between items-center">
            <div>
              <h2 className="text-2xl font-bold">Delivery Fee Management</h2>
              <p className="text-sm opacity-90 mt-1">Set delivery fees for different landmarks</p>
            </div>
            <button
              onClick={onClose}
              className="text-crust hover:text-gray-700 text-2xl font-bold"
            >
              ×
            </button>
          </div>
        </div>

        <div className="p-6 overflow-y-auto max-h-[calc(90vh-200px)]">

          {/* Current Fees Display */}
          {currentFees.length > 0 && (
            <div className="mb-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-3">Current Delivery Fees</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2 md:gap-3">
                {currentFees.map((fee, index) => (
                  <div key={index} className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                    <div className="flex justify-between items-start gap-2">
                      <div>
                        <p className="font-medium text-gray-900 text-sm md:text-base">
                          {fee.landmark || 'Default Fee'}
                        </p>
                        {fee.isDefault && (
                          <span className="text-xs md:text-sm text-primary-600 font-medium">Default</span>
                        )}
                      </div>
                      <span className="text-base md:text-lg font-bold text-green-600 whitespace-nowrap">
                        {formatCurrency(fee.fee)}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Add New Fees Section */}
          <div className="mb-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <MapPinIcon className="w-5 h-5 mr-2" />
              Add Landmark-Specific Fees
            </h3>

            {/* University Landmarks List */}
            <div className="space-y-4 mb-6">
              {landmarks.reduce((acc, landmark) => {
                // Group landmarks by university
                const university = acc.find(u => u.name === landmark.university);
                if (university) {
                  university.landmarks.push(landmark);
                } else {
                  acc.push({
                    name: landmark.university,
                    landmarks: [landmark]
                  });
                }
                return acc;
              }, []).map((university, index) => (
                <div key={index} className="bg-gray-50 rounded-lg p-3 md:p-4">
                  <h4 className="font-semibold text-gray-900 mb-2 md:mb-3 text-sm md:text-base">
                    {university.name}
                  </h4>
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2 md:gap-3">
                    {university.landmarks.map((landmark, lIndex) => {
                      const isAdded = newFees.some(fee => fee.landmark === landmark.name) ||
                        currentFees.some(fee => fee.landmark === landmark.name);
                      const currentFee = currentFees.find(fee => fee.landmark === landmark.name)?.fee;
                      return (
                        <div
                          key={lIndex}
                          className={`p-2 md:p-3 rounded-lg text-left transition-all flex items-start justify-between gap-2 ${
                            isAdded
                              ? 'bg-gray-100 cursor-not-allowed'
                              : 'bg-white hover:bg-cheese hover:text-crust border border-gray-200 hover:border-transparent'
                          }`}
                          style={{ position: 'relative' }}
                        >
                          <div>
                            <span className="font-medium text-sm md:text-base">{landmark.name}</span>
                            {isAdded && (
                              <p className="text-xs text-gray-600 mt-1">
                                Current fee: {formatCurrency(currentFee || 0)}
                              </p>
                            )}
                          </div>
                          {isAdded ? (
                            <span
                              role="button"
                              tabIndex={0}
                              onClick={(e) => {
                                e.stopPropagation();
                                const fee = currentFees.find(f => f.landmark === landmark.name);
                                if (fee) {
                                  setNewFees(prev => [...prev, {
                                    landmark: landmark.name,
                                    fee: fee.fee.toString(),
                                    university: landmark.university
                                  }]);
                                }
                              }}
                              className="text-yellow-600 hover:text-yellow-700 cursor-pointer outline-none"
                              style={{ display: 'inline-flex', alignItems: 'center' }}
                            >
                              <PencilIcon className="w-4 h-4 md:w-5 md:h-5 flex-shrink-0" />
                            </span>
                          ) : (
                            <button
                              onClick={() => addFee(landmark)}
                              className="bg-transparent border-none p-0 m-0 cursor-pointer"
                              style={{ display: 'inline-flex', alignItems: 'center' }}
                            >
                              <PlusIcon className="w-4 h-4 md:w-5 md:h-5 flex-shrink-0" />
                            </button>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>

            {/* Search for specific landmark */}
            <div className="relative mb-4">
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  handleSearch(e.target.value);
                }}
                className="w-full px-4 py-3 pl-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cheese focus:border-transparent"
                placeholder="Search for specific landmark..."
              />
              <MagnifyingGlassIcon className="w-5 h-5 text-gray-400 absolute left-3 top-3.5" />
              {searchLoading && (
                <div className="absolute right-3 top-3.5">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-cheese"></div>
                </div>
              )}
            </div>

            {/* Search Results */}
            <AnimatePresence>
              {searchResults.length > 0 && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  className="bg-white border border-gray-200 rounded-lg max-h-48 overflow-y-auto mb-4"
                >
                  {searchResults.map((landmark, index) => {
                    const isAdded = newFees.some(fee => fee.landmark === landmark.name) ||
                                  currentFees.some(fee => fee.landmark === landmark.name);
                    
                    return (
                      <button
                        key={index}
                        onClick={() => !isAdded && addFee(landmark)}
                        disabled={isAdded}
                        className={`w-full px-4 py-3 text-left hover:bg-gray-50 border-b border-gray-100 last:border-b-0 flex items-center justify-between ${
                          isAdded ? 'bg-gray-50 cursor-not-allowed' : ''
                        }`}
                      >
                        <div>
                          <p className="font-medium text-gray-900">{landmark.name}</p>
                          <p className="text-sm text-gray-500">{landmark.university}</p>
                        </div>
                        {isAdded ? (
                          <CheckCircleIcon className="w-5 h-5 text-green-500" />
                        ) : (
                          <PlusIcon className="w-5 h-5 text-cheese" />
                        )}
                      </button>
                    );
                  })}
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* New Fees List */}
          {newFees.length > 0 && (
            <div className="mb-6">
              <h4 className="text-md font-semibold text-gray-900 mb-3">New Fees to Add</h4>
              <div className="space-y-3">
                {newFees.map((fee, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="bg-yellow-50 border border-yellow-200 rounded-lg p-4"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <p className="font-medium text-gray-900">{fee.landmark}</p>
                        <p className="text-sm text-gray-500">{fee.university}</p>
                      </div>
                      <div className="flex items-center space-x-3">
                        <div className="flex items-center space-x-2">
                          <span className="text-sm font-medium text-gray-700">₦</span>
                          <input
                            type="number"
                            value={fee.fee}
                            onChange={(e) => updateFeeAmount(index, e.target.value)}
                            className="w-24 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cheese focus:border-transparent"
                            placeholder="500"
                            min="0"
                          />
                        </div>
                        <button
                          onClick={() => removeFee(index)}
                          className="text-red-500 hover:text-red-700"
                        >
                          <TrashIcon className="w-5 h-5" />
                        </button>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          )}

        </div>

        {/* Footer */}
        <div className="bg-gray-50 px-4 py-3 md:px-6 md:py-4 border-t border-gray-200">
          <div className="flex flex-col-reverse md:flex-row gap-2 md:gap-3 md:justify-end">
            <button
              onClick={onClose}
              className="w-full md:w-auto px-4 md:px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-100 transition-colors text-center"
            >
              Cancel
            </button>
            <button
              onClick={saveFees}
              disabled={saving || (newFees.length === 0 && !defaultFee)}
              className={`w-full md:w-auto px-4 md:px-6 py-2 rounded-lg font-medium transition-colors flex items-center justify-center space-x-2 ${
                saving || (newFees.length === 0 && !defaultFee)
                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  : 'bg-cheese text-crust hover:bg-yellow-500'
              }`}
            >
              {saving ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current"></div>
                  <span>Saving...</span>
                </>
              ) : (
                <>
                  <CheckCircleIcon className="w-4 h-4" />
                  <span>Save Delivery Fees</span>
                </>
              )}
            </button>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default DeliveryFeeManager;
